import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { AppleDeviceProfileResolverActions, selectProfileItemsByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import {
  PaneComponent,
  PanelComponent,
  PanelFooterComponent,
  SkSpinner3Component,
  YesNoIconComponent,
} from '@rocksoft/cdk';
import { Certificate } from '@rocksoft/mdm-sdk-v2';
import { isNotNullOrUndefined } from '@rocksoft/shared';
import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AppleCommandProgressService } from '../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../utilities/command-spinner/command-spinner.component';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';
@Component({
  selector: 'ml-mdm-device-certificates',
  templateUrl: './device-certificates.container.html',
  styleUrls: ['./device-certificates.container.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PanelComponent,
    PaneComponent,
    SkSpinner3Component,
    PanelFooterComponent,
    FaIconComponent,
    CommandSpinnerComponent,
    YesNoIconComponent,
    AsyncPipe,
  ],
})
export class DeviceCertificatesComponent {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly udid = signal('');

  public readonly udid$: Observable<string> = toObservable(this.udid).pipe(
    filter(isNotNullOrUndefined),
    distinctUntilChanged()
  );
  public readonly certificates$: Observable<Certificate[]> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileItemsByType('certificate')))
  );

  private _commandProgress = new Subject<CommandProgress>();
  public readonly commandProgress$: Observable<CommandProgress> = this.udid$.pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'CertificateList'))
    )
  );
  constructor() {
    effect((onCleanup) => {
      const params$ = this.route.parent?.params;
      if (!params$) {
        this.udid.set('');
        return;
      }
      const sub = params$
        .pipe(
          map((p: Params) => p.udid),
          filter((_udid) => !!_udid),
          distinctUntilChanged()
        )
        .subscribe((_udid) => {
          this.udid.set(_udid);
        });

      onCleanup(() => {
        sub.unsubscribe();
      });
    });

    effect((onCleanup) => {
      const _udid = this.udid();
      if (!_udid) return;
      const sub = this.commandProgress$
        .pipe(
          filter((progress) => progress.status === 'Acknowledged'),
          distinctUntilChanged()
        )
        .subscribe((progress) => {
          this.store.dispatch(
            AppleDeviceProfileResolverActions.profileRequested({
              udid: progress.udid,
              profileType: 'certificate',
            })
          );
        });

      onCleanup(() => {
        sub.unsubscribe();
      });
    });
  }
  refresh(): void {
    const _udid = this.udid();
    if (!_udid) return;
    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'CertificateList',
    };
    this._commandProgress.next(command);
    this.mdmService.getCertificates(_udid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
